'use client';

import React, { createContext, useContext, useState } from 'react';
import { cn } from '../../../lib/utils';
import { CloseIcon, CheckIcon, ArrowChevronIcon } from '../../../icons';

// ============================================
// Context
// ============================================
type SelectModalContextValue = {
  value: string | null;
  onChange: (value: string) => void;
  onClose: () => void;
};

const SelectModalContext = createContext<SelectModalContextValue | null>(null);

const useSelectModalContext = () => {
  const context = useContext(SelectModalContext);
  if (!context) {
    throw new Error('SelectModal.Option must be used within SelectModal');
  }
  return context;
};

// ============================================
// SelectModal.Option
// ============================================
type SelectModalOptionProps = {
  value: string;
  children: React.ReactNode;
  disabled?: boolean;
  className?: string;
};

const SelectModalOption: React.FC<SelectModalOptionProps> = ({
  value,
  children,
  disabled = false,
  className,
}) => {
  const { value: selectedValue, onChange, onClose } = useSelectModalContext();
  const isSelected = selectedValue === value;

  const handleClick = () => {
    if (disabled) return;
    onChange(value);
    onClose();
  };

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={handleClick}
      className={cn(
        'w-full flex items-center justify-between',
        'px-3 py-2.5 md:px-4 md:py-3',
        'text-sm md:text-base text-left transition-colors duration-150',
        'border-b border-gscale-100 last:border-b-0',
        !disabled && 'hover:bg-gscale-50 cursor-pointer',
        isSelected && 'bg-gscale-50',
        disabled && 'opacity-50 cursor-not-allowed',
        className,
      )}
    >
      <div className="flex-1 min-w-0">{children}</div>
      {isSelected && (
        <CheckIcon className="w-4 h-4 md:w-5 md:h-5 text-gtred flex-shrink-0 ml-2 md:ml-3" />
      )}
    </button>
  );
};

SelectModalOption.displayName = 'SelectModal.Option';

// ============================================
// SelectModal (Main Component)
// ============================================
type SelectModalProps = {
  children: React.ReactNode;
  title?: string;
  value: string | null;
  onChange: (value: string) => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  maxWidth?: string;
  maxHeight?: string;
  className?: string;
};

const SelectModalRoot: React.FC<SelectModalProps> = ({
  children,
  title,
  value,
  onChange,
  open: controlledOpen,
  onOpenChange,
  maxWidth = '400px',
  maxHeight = '60vh',
  className,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);

  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;

  const handleOpenChange = (newOpen: boolean) => {
    if (!isControlled) {
      setInternalOpen(newOpen);
    }
    onOpenChange?.(newOpen);
  };

  const handleClose = () => handleOpenChange(false);

  if (!isOpen) return null;

  return (
    <SelectModalContext.Provider
      value={{ value, onChange, onClose: handleClose }}
    >
      <div
        className="fixed inset-0 bg-gscale-950/50 flex items-center justify-center z-[99999] p-4 md:p-6"
        onClick={handleClose}
      >
        <div
          className={cn(
            'w-full bg-white rounded-lg md:rounded-xl shadow-xl',
            'border border-gscale-200',
            'flex flex-col',
            'animate-in fade-in-0 zoom-in-95 duration-200',
            className,
          )}
          style={{ maxWidth }}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 md:px-5 md:py-4 border-b border-gscale-200">
            <h3 className="text-base md:text-lg font-semibold text-gscale-900">
              {title ?? '선택'}
            </h3>
            <button
              type="button"
              onClick={handleClose}
              className="p-1 rounded-md text-gscale-500 hover:text-gscale-700 hover:bg-gscale-100 transition-colors"
            >
              <CloseIcon className="w-4 h-4 md:w-5 md:h-5" />
            </button>
          </div>

          {/* Options */}
          <div className="overflow-y-auto" style={{ maxHeight }}>
            {children}
          </div>
        </div>
      </div>
    </SelectModalContext.Provider>
  );
};

SelectModalRoot.displayName = 'SelectModal';

// ============================================
// SelectModal.Trigger
// ============================================
type SelectModalTriggerProps = {
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
};

const SelectModalTrigger: React.FC<SelectModalTriggerProps> = ({
  children,
  className,
  onClick,
}) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center justify-between w-full',
        'px-3 py-2.5 md:px-4 md:py-3',
        'border border-gscale-200 rounded-md md:rounded-lg',
        'bg-white hover:bg-gscale-50 transition-colors',
        'text-left text-sm md:text-base text-gscale-900',
        className,
      )}
    >
      {children}
      <ArrowChevronIcon className="w-4 h-4 md:w-5 md:h-5 text-gscale-500 flex-shrink-0" />
    </button>
  );
};

SelectModalTrigger.displayName = 'SelectModal.Trigger';

// ============================================
// Compound Component Export
// ============================================
export const SelectModal = Object.assign(SelectModalRoot, {
  Option: SelectModalOption,
  Trigger: SelectModalTrigger,
});

export type {
  SelectModalProps,
  SelectModalOptionProps,
  SelectModalTriggerProps,
};
